"use client";

import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { useState } from "react";

const DISABLED_EXTERNAL_LINK_DOCS = new Set([
  "VEN129",
  "VEN130",
  "VEN131",
  "CUB1",
  "CUB2",
  "CUB3",
  "RUS1",
  "RUS2",
  "RUS3",
  "RUS4",
  "RUS5",
  "RUS6",
  "RUS7",
  "RUS8",
  "RUS9",
  "RUS10",
  "RUS12",
  "RUS13",
  "RUS14",
  "RUS15",
  "RUS16",
  "RUS17",
  "RUS18",
  "RUS19",
  "RUS20",
  "RUS21",
  "RUS22",
]);

export default function TitleDetails({ docid }: { docid: string }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const router = useRouter();

  const [title, setTitle] = useState<string>("Document Details");
  const [sourceURL, setSourceURL] = useState<string>("");
  const [externalWarningOpen, setExternalWarningOpen] = useState(false);
  const isExternalDisabled = DISABLED_EXTERNAL_LINK_DOCS.has(docid);

  const onBackClick = () => {
    router.back();
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await fetch(`/api/details/${docid}`, { method: "GET" });
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        const data = await res.json();
        setTitle(data.document_title || "Document Details");
        setSourceURL(data.source_url || "");
      } catch (err) {
        console.error("Failed to fetch boxes:", err);
      }
    };
    fetchDetails();
  }, [docid]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgcolor="white"
      width="100%"
      overflow="hidden"
      flexWrap="nowrap"
    >
      <Box
        display="flex"
        alignItems="center"
        bgcolor="primary.dark"
        px={isSmall ? 1.5 : 2}
        py={1}
        minWidth={isSmall ? "48px" : "160px"}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={onBackClick}
      >
        <Icon
          icon="material-symbols:arrow-back"
          color="white"
          width={isSmall ? 24 : 28}
          height={isSmall ? 24 : 28}
        />
        {!isSmall && (
          <Typography variant="h6" color="white" fontWeight={400} ml={2} noWrap>
            Back
          </Typography>
        )}
      </Box>

      {/* Title and docid display */}
      <Box flexGrow={1} textAlign="center" px={2} minWidth={0}>
        <Typography
          component="h1"
          variant={isSmall ? "h6" : "h5"}
          color="black"
          fontWeight={400}
          noWrap
          sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        minWidth={isSmall ? "80px" : "160px"}
        justifyContent="center"
      >
        <IconButton
          aria-label="Open document in a new tab"
          size={isSmall ? "small" : "medium"}
          disabled={isExternalDisabled}
          onClick={
            isExternalDisabled ? undefined : () => setExternalWarningOpen(true)
          }
          sx={{
            opacity: isExternalDisabled ? 0.4 : 1,
            pointerEvents: isExternalDisabled ? "none" : "auto",
          }}
        >
          <Icon
            icon={
              isExternalDisabled ? "mdi:link-off" : "octicon:link-external-16"
            }
            width={isSmall ? 20 : 28}
            height={isSmall ? 20 : 28}
            aria-hidden="true"
          />
        </IconButton>

        <Dialog
          open={externalWarningOpen}
          onClose={() => setExternalWarningOpen(false)}
          aria-labelledby="external-link-warning-title"
          maxWidth="md"
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: 2,
              p: 1,
              width: isSmall ? "100%" : "650px",
            },
          }}
        >
          <DialogTitle
            id="external-link-warning-title"
            sx={{ fontWeight: 600, fontSize: "1.25rem" }}
          >
            External Link Warning
          </DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              You are leaving our site and entering a third-party website.
              Please note that this external site:
            </Typography>
            <List dense disablePadding sx={{ pl: 2 }}>
              <ListItem
                sx={{
                  display: "list-item",
                  listStyleType: "disc",
                  pl: 0,
                  fontSize: "1rem",
                }}
              >
                <ListItemText
                  slotProps={{ primary: { fontSize: "1rem" } }}
                  primary="May not follow the same accessibility or privacy standards as our website."
                />
              </ListItem>
              <ListItem
                sx={{
                  display: "list-item",
                  listStyleType: "disc",
                  pl: 0,
                  fontSize: "1rem",
                }}
              >
                <ListItemText
                  slotProps={{ primary: { fontSize: "1rem" } }}
                  primary="Is not under our control or guarantee."
                />
              </ListItem>
            </List>
            <Typography mt={1}>Do you wish to proceed?</Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setExternalWarningOpen(false)}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              component="a"
              href={sourceURL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setExternalWarningOpen(false)}
            >
              Proceed
            </Button>
          </DialogActions>
        </Dialog>

        <IconButton
          component="a"
          href={`/api/pdf/${docid}`}
          download={`${docid}.pdf`}
          aria-label="Download document PDF"
          size={isSmall ? "small" : "medium"}
        >
          <Icon
            icon="typcn:download"
            width={isSmall ? 20 : 28}
            height={isSmall ? 20 : 28}
            aria-hidden="true"
          />
        </IconButton>
      </Box>
    </Box>
  );
}
